import { format, formatRelative, isWithinInterval, subDays } from "date-fns";
import { Fragment, useState } from "react";

import { useGetAuthUser } from "@/api/get-user";
import { getUserInitials } from "@/app/utils/get-user-initials";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useOrganisation } from "@/features/select-organisation/api/get-my-organisation";
import {
  PublicRecordingComment,
  RoleEnum,
  RoleGroupEnum,
} from "@classview/shared-types";

import { parseContentWithTimestamps } from "../utils/timestamp-click";

import { AddComment } from "./add-comment";
import { CommentRowActions } from "./comment-row-actions";
import { EditComment } from "./edit-comment";
import { Replies } from "./replies";

type CommentItemProps = {
  comment: PublicRecordingComment;
  handleJumpToTimestamp: (timestamp: number) => void;
};

export function CommentItem({
  comment,
  handleJumpToTimestamp,
}: CommentItemProps) {
  const userName = comment.user.name.split(" ");
  const relativeDate = formatRelative(comment.createdAt, new Date());
  const { hasPermission, organisation } = useOrganisation();
  const [isEditing, setIsEditing] = useState(false);

  const isLast5Days = isWithinInterval(comment.createdAt, {
    start: subDays(new Date(), 5),
    end: new Date(),
  });

  const { data: user } = useGetAuthUser();

  const organisationHasCommentPermission = hasPermission([
    RoleEnum.REPLAY,
    RoleEnum.REPLAY_ADVANCED,
  ]);

  const shouldShowReplyButton =
    comment.parentCommentId === null &&
    comment.content !== "[deleted]" &&
    organisationHasCommentPermission;

  const shouldShowReplies = comment.parentCommentId === null;

  const canDeleteComment =
    (organisation.roleGroup === RoleGroupEnum.ADMIN ||
      hasPermission(RoleEnum.SUPER_ADMIN || user?.sub === comment.user.id)) &&
    comment.content !== "[deleted]";

  return (
    <div className="flex flex-row gap-2">
      <Avatar>
        {comment.user.avatarURL && <AvatarImage src={comment.user.avatarURL} />}
        <AvatarFallback className="select-none bg-border capitalize text-muted">
          {getUserInitials(userName[0], userName[1])}
        </AvatarFallback>
      </Avatar>
      <div className="w-full">
        <div className="flex flex-row gap-2">
          <div className="flex-1">
            <p className="text-sm">
              <span className="mr-1 font-medium text-foreground">
                {comment.user.name}
              </span>
              <span className="text-xs text-muted-foreground">
                {isLast5Days ? relativeDate : format(comment.createdAt, "PP")}
              </span>
            </p>
            {isEditing ? (
              <EditComment comment={comment} setIsEditing={setIsEditing} />
            ) : (
              <p className="mb-1 text-sm text-secondary">
                {parseContentWithTimestamps(
                  comment.content,
                  handleJumpToTimestamp,
                ).map((part, index) => (
                  <Fragment key={index}>{part}</Fragment>
                ))}
              </p>
            )}
          </div>
          {canDeleteComment && (
            <div className="ml-auto">
              <CommentRowActions
                commentId={comment.id}
                setIsEditing={setIsEditing}
              />
            </div>
          )}
        </div>
        {shouldShowReplyButton && (
          <AddComment parentCommentId={comment.id} className="max-w-80 p-0" />
        )}
        {shouldShowReplies && (
          <Replies
            parentCommentId={comment.id}
            handleJumpToTimestamp={handleJumpToTimestamp}
          />
        )}
      </div>
    </div>
  );
}
